/* Submagic widget — Shadow DOM scoped. Vanilla CSS (no Chakra) but matches
   the brand palette and spacing scale used in app/theme.tsx. */

:host {
  --sm-fg: #0f0f1a;
  --sm-fg-muted: #6b7280;
  --sm-fg-subtle: #9ca3af;
  --sm-bg: #ffffff;
  --sm-bg-subtle: #f8f8fa;
  --sm-border: #e5e6e8;
  --sm-primary: #ff5a1f;
  --sm-primary-hover: #d03801;
  --sm-primary-subtle: rgba(255, 90, 31, 0.08);
  --sm-primary-soft-border: #fed5c7;
  --sm-error: #d23030;
  --sm-error-subtle: rgba(210, 48, 48, 0.08);
}

.widget {
  font-family:
    "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  max-width: 480px;
  margin: 0 auto;
  padding: 28px;
  background: var(--sm-bg);
  border: 1px solid var(--sm-border);
  border-radius: 16px;
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.04),
    0 12px 32px rgba(0, 0, 0, 0.06);
  color: var(--sm-fg);
  -webkit-font-smoothing: antialiased;
}

.widget *,
.widget *::before,
.widget *::after {
  box-sizing: border-box;
}

/* ---------- Dropzone ---------- */

.dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border: 1.5px dashed var(--sm-primary-soft-border);
  border-radius: 12px;
  padding: 40px 24px;
  cursor: pointer;
  background: var(--sm-bg-subtle);
  transition: all 0.2s ease-in-out;
}

.dropzone:hover,
.dropzone.drag-over {
  border-color: var(--sm-primary);
  background: var(--sm-primary-subtle);
}

.dropzone-icon {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--sm-primary);
  color: #fff;
  border-radius: 999px;
}

.dropzone-icon svg {
  width: 22px;
  height: 22px;
}

.dropzone-label {
  color: var(--sm-fg);
  font-weight: 600;
  font-size: 15px;
  line-height: 1.4;
}

.dropzone-hint {
  color: var(--sm-fg-subtle);
  font-size: 12px;
  line-height: 1.4;
}

/* ---------- Status (validating / authenticating / preparing) ---------- */

.status {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 32px 16px;
}

.spinner {
  width: 28px;
  height: 28px;
  border: 2.5px solid var(--sm-primary-soft-border);
  border-top-color: var(--sm-primary);
  border-radius: 50%;
  animation: sm-spin 0.8s linear infinite;
}

@keyframes sm-spin {
  to {
    transform: rotate(360deg);
  }
}

.status-label {
  color: var(--sm-fg);
  font-size: 14px;
  font-weight: 500;
  text-align: center;
}

.status-sub {
  color: var(--sm-fg-muted);
  font-size: 12px;
  text-align: center;
  max-width: 320px;
}

/* ---------- Progress ---------- */

.progress-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 24px 8px;
}

.progress {
  width: 100%;
  max-width: 320px;
  height: 6px;
  background: var(--sm-border);
  border-radius: 999px;
  overflow: hidden;
}

.progress > div {
  height: 100%;
  background: var(--sm-primary);
  border-radius: 999px;
  transition: width 0.3s ease;
}

.progress-meta {
  display: flex;
  justify-content: space-between;
  width: 100%;
  max-width: 320px;
  font-size: 12px;
  color: var(--sm-fg-muted);
}

.progress-meta strong {
  color: var(--sm-fg);
  font-weight: 600;
}

/* ---------- Preview ---------- */

.preview-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.preview-video {
  width: 100%;
  max-width: 320px;
  aspect-ratio: 9 / 16;
  border-radius: 12px;
  display: block;
  border: 0;
  background: #000;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  object-fit: contain;
}

.button-row {
  display: flex;
  gap: 12px;
  width: 100%;
  max-width: 320px;
}

.button-row .cta {
  flex: 1;
}

/* ---------- CTA / buttons ---------- */

.cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: var(--sm-primary);
  color: #fff;
  padding: 12px 24px;
  border-radius: 10px;
  font-family: inherit;
  font-weight: 600;
  font-size: 14px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.cta:hover {
  background: var(--sm-primary-hover);
}

.cta:focus-visible {
  outline: 2px solid var(--sm-primary);
  outline-offset: 2px;
}

.cta--ghost {
  background: transparent;
  color: var(--sm-fg);
  border: 1px solid var(--sm-border);
}

.cta--ghost:hover {
  background: var(--sm-bg-subtle);
  border-color: var(--sm-fg-muted);
}

/* ---------- Error ---------- */

.error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 16px;
}

.error-message {
  color: var(--sm-error);
  background: var(--sm-error-subtle);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
  max-width: 320px;
}
